import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import LeadForm from "../components/site/LeadForm";
import WhatsAppButton from "../components/site/WhatsAppButton";

export function AclPillar() {
  return (
    <div>
      <section className="container-wide pt-16 pb-8 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <div className="eyebrow mb-3">Pillar guide</div>
          <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">ACL Rehabilitation in Delhi — the complete patient guide.</h1>
          <p className="text-brand-ink2 mt-5">Whether surgery is planned, recent or months behind you, a well-structured rehabilitation programme is the biggest predictor of a strong return to sport, work and life.</p>
          <div className="mt-6 flex gap-3">
            <Link to="/book" className="btn-primary">Book ACL Assessment <ArrowRight size={16}/></Link>
            <WhatsAppButton message="Hello Physio Hyve, I would like to ask about ACL rehabilitation.">Ask about ACL rehab</WhatsAppButton>
          </div>
        </div>
        <aside className="md:sticky md:top-24 self-start">
          <LeadForm category="acl" sourcePage="acl-pillar" defaultConcern="I want to discuss ACL rehab"/>
        </aside>
      </section>
      <section className="container-wide pb-16 md:pr-[calc(33%+2.5rem)]">
        <div className="prose-hyve">
          <h2>Pre-operative rehabilitation ("prehab")</h2>
          <p>Prehab focuses on reducing swelling, restoring range of motion, activating the quadriceps and preparing the body — and the mind — for surgery. Patients who enter surgery in better condition tend to recover faster.</p>
          <h2>Early post-operative phase (0–6 weeks)</h2>
          <ul>
            <li>Swelling and pain management</li>
            <li>Restoring full knee extension</li>
            <li>Quadriceps activation and control</li>
            <li>Safe, progressive weight-bearing</li>
            <li>Gait re-education</li>
          </ul>
          <h2>Strength phase (6–14 weeks)</h2>
          <p>Structured strength progression for the quads, glutes and calves — the muscle groups most tied to safe return-to-sport testing outcomes.</p>
          <h2>Movement control and running (3–6 months)</h2>
          <p>Progressive introduction of running, cutting, plyometrics and change-of-direction work — always earned by objective criteria, not the calendar.</p>
          <h2>Return-to-sport testing</h2>
          <p>Time alone should not clear you for sport. Objective testing (strength symmetry, hop batteries, movement quality and psychological readiness) offers a far better guide.</p>
          <h2>Common mistakes we see</h2>
          <ul>
            <li>Stopping rehab too early because pain has settled</li>
            <li>Neglecting quadriceps strength deficits</li>
            <li>Rushing return-to-sport before objective criteria are met</li>
            <li>Underestimating psychological readiness</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

export function NeuroPillar() {
  return (
    <div>
      <section className="container-wide pt-16 pb-8 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <div className="eyebrow mb-3">Pillar guide</div>
          <h1 className="font-heading text-4xl md:text-6xl text-brand-ink leading-[1.05]">Neurological Rehabilitation in Delhi — a patient and family guide.</h1>
          <p className="text-brand-ink2 mt-5">Neuro rehab is intensive, personalised and dependent on consistency. We work with you and your family to build meaningful, functional recovery.</p>
          <div className="mt-6 flex gap-3">
            <Link to="/book" className="btn-primary">Book Neuro Assessment <ArrowRight size={16}/></Link>
            <WhatsAppButton message="Hello Physio Hyve, I would like information about neurological rehabilitation for a family member.">Ask about neuro rehab</WhatsAppButton>
          </div>
        </div>
        <aside className="md:sticky md:top-24 self-start">
          <LeadForm category="neuro" sourcePage="neuro-pillar" defaultConcern="Neuro rehab enquiry"/>
        </aside>
      </section>
      <section className="container-wide pb-16 md:pr-[calc(33%+2.5rem)]">
        <div className="prose-hyve">
          <h2>Who we work with</h2>
          <ul>
            <li>Stroke rehabilitation</li>
            <li>Balance and gait rehabilitation</li>
            <li>Functional movement training</li>
            <li>Selected neurological conditions within our scope of practice</li>
          </ul>
          <h2>What sessions look like</h2>
          <p>Task-specific training, progressive strength work, balance and coordination, and home programmes tailored to family involvement. We're honest about outcomes — physiotherapy does not cure neurological disease, but it can meaningfully improve function.</p>
          <h2>How families can support</h2>
          <ul>
            <li>Consistency with home exercises</li>
            <li>Safe environmental setup</li>
            <li>Encouragement during setbacks</li>
            <li>Attending sessions where possible</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
